import { Manifest } from '../../internal/Manifest';
import {
  NetworkRequestObservation,
  TrackInfo,
  DrmScheme,
  MediaType,
  PlaybackStatistics,
  RequestType,
  SeekRangeInfo,
  PlaybackOptions,
  TrackVariantInfo,
  ThumbnailPreviewMetadata,
  TrackKind,
  TrackType,
} from '@quickplay/player-interface';
import { PlayerAdapter } from '../../internal/PlayerAdapter';
import { ShakaManifestAdapter } from './ShakaManifestAdapter';
import {
  Logger,
  AnyFunc,
  createEventEmitter,
  EventEmitter,
  Error,
  ErrorCodes,
  createError,
  PlatformUtil,
  createSafeTimeout,
  TimeoutController,
} from '@quickplay/foundation';
// Import Shaka Utilities and the ShakaPreloadManager
import { ShakaPreloadManager } from './ShakaPreloadManager';
import { configureLicenseAndCertificate, getMimeType, getKeySystemForDrmScheme } from './ShakaUtils';

import { convertToPlayerError } from './ShakaErrorHandler';
import shaka from 'shaka-player';
import { BufferedAmount, getBufferedAmount } from './VideoUtils';
import { PlayerErrorCodes } from '../../PlayerErrorCodes';
import { SidecarTrack } from '../../PlayerBuilder';

const DEFAULT_STALL_TIMEOUT_MS = 30 * 1000;
const DEFAULT_CONNECTION_TIMEOUT_MS = 30 * 1000;
const DESTROY_TIMEOUT_MS = 1_000;

// TODO: determine what to do with these; Found that these are used
//  in /content/platform/js/verimatrix.js
//  and /content/webtv/js/player/verimatrix.js
export const StringUtils = shaka.util.StringUtils;
export const Uint8ArrayUtils = shaka.util.Uint8ArrayUtils;
export const BufferUtils = shaka.util.BufferUtils;

export class ShakaPlayerAdapter implements PlayerAdapter {
  private shakaPlayer_: any;
  private manifest_: Manifest;
  private emitter_: EventEmitter;
  private timeoutController: TimeoutController;
  private initialPlaybackTime_?: number;
  private requestInterceptor_: ((requestType: RequestType, request: any) => void) | undefined;
  private responseInterceptor_: ((requestType: RequestType, response: any) => void) | undefined;
  private thumbanailTrack_: any;
  private preloadManager_: any;
  private isPlayingInLive_: boolean = false;
  private isUnloaded_: boolean = false;
  private isRecoveringLicense_: boolean = false;
  private licenseRenewalAttempts_: number = 0;
  private static readonly MAX_LICENSE_RENEWAL_ATTEMPTS = 1;
  // Tracks consecutive manifest 'retry' events matching stopPlaybackOnManifestHttpErrorCodes,
  // so we only raise 'manifestHttpError' once Shaka has exhausted all configured retry attempts,
  // instead of on the very first retry. Reset on successful manifest load/update.
  private manifestRetryAttemptCount_: number = 0;

  constructor(
    private videoElement_: HTMLVideoElement,
    private logger_: Logger,
    private contentUrl_: string,
    private playbackOptions_: PlaybackOptions,
    private licenseUrl_?: string,
    private drmScheme_?: DrmScheme,
    private mediaType_?: MediaType,
    private serverCertificate_?: ArrayBuffer,
    private vttTracks_?: SidecarTrack[],
    private videoWrapper_?: HTMLElement,
    private shakaLibrary: any = shaka,
    private webVttThumbnailUrl_?: string,
    private enablePreload?: boolean,
    externalShakaPlayer?: any,
  ) {
    this.enablePreload = this.playbackOptions_.enablePreload || false;
    this.emitter_ = createEventEmitter();
    this.shakaPlayer_ = externalShakaPlayer ?? this.createShakaPlayer();
    this.manifest_ = this.createManifest_();
    this.addShakaEventListeners_();
    this.registerNetworkFilters_();
    this.setLoadConfigurations_();
    this.setNetworkInterceptors_();
    // Note: To create an safe timeout instance
    this.timeoutController = createSafeTimeout();
    // TODO: Add the uncompiled shaka library for debugging
    // this.setLoggerLevel_();

    // Configure DRM if license URL is provided
    if (this.licenseUrl_) {
      configureLicenseAndCertificate({
        player: this.shakaPlayer_,
        logger: this.logger_,
        certificate: this.serverCertificate_,
        licenseUrl: this.licenseUrl_,
        playbackOptions: this.playbackOptions_,
        shakaLibrary: this.shakaLibrary,
        shakaVersion: this.shakaLibrary.Player.version,
        drmScheme: this.drmScheme_,
      });
    }

    // Initialize preload manager if enabled
    if (!!this.enablePreload) {
      this.logger_.log('Initializing preload functionality');
      this.initializePreload();
    }

    this.addVideoEventListeners();

    if (this.videoWrapper_) {
      // Note: This forces Shaka to use UITextDisplayer instead of SimpleTextDisplayer
      // Applications that prefer custom rendering of captions instead of Native cue rendering could use this.
      this.shakaPlayer_.setVideoContainer(videoWrapper_);
    }

    /**
     * PS5 DRM Fix: Skip polyfill installation on PS5 platform
     * Issue: Per-instance polyfill installation causes error 6002 after 20+ consecutive playbacks on PS5.
     * Mitigation: Skip polyfill installation on PS5 for every playback; install once globally at app startup instead.
     * Deep-dive RCA needed to understand exact memory leak or race condition in ShakaPlayer Adapter
     * causing mediaKeys to not clear properly on PS5 between consecutive playback sessions.
     */
    this.shakaLibrary.polyfill.installAll();

    // Note: From v4.x, by default Shaka Player supports Modern EME but our QP streams currently doesn't support Modern EME yet.
    // We can use legacy Apple Media Keys with installing PatchedMediaKeysApple polyfill.

    // TODO:
    // Shaka player providing an option to support both legacy and Modern EME. We should explore a way to implement the multiple DRM providers support.
    // Refer: [https://shaka-player-demo.appspot.com/docs/api/tutorial-fairplay.html]

    this.shakaLibrary.polyfill.PatchedMediaKeysApple.install();
  }

  public get textTrackIsVisible(): boolean {
    return this.shakaPlayer_.isTextTrackVisible();
  }

  public set textTrackIsVisible(visible: boolean) {
    if (this.shakaPlayer_.isTextTrackVisible() !== visible) {
      this.shakaPlayer_.setTextTrackVisibility(visible);
    }
  }

  public get buffered(): BufferedAmount {
    return getBufferedAmount(this.videoElement_);
  }

  public get internalPlayerName(): string {
    return 'shaka-player';
  }

  public get rawLibrary(): any {
    return this.shakaLibrary;
  }

  public get rawPlayer(): any {
    return this.shakaPlayer_;
  }

  public get internalPlayerVersion(): string {
    return this.shakaLibrary.Player.version;
  }

  public get minBitrate(): number {
    return this.manifest_.getMinBandwidth();
  }

  public set minBitrate(bitrate: number) {
    this.manifest_.setMinBandwidth(bitrate);
  }

  public get maxBitrate(): number {
    return this.manifest_.getMaxBandwidth();
  }

  public set maxBitrate(bitrate: number) {
    this.manifest_.setMaxBandwidth(bitrate);
  }

  public get availableBitrates(): number[] {
    return this.manifest_.getAvailableBitrates();
  }

  public get bufferBehind(): number {
    return this.manifest_.getBufferBehind();
  }

  public get playbackRate(): number {
    return this.manifest_.getPlaybackRate();
  }

  public set playbackRate(rate: number) {
    this.manifest_.setPlaybackRate(rate);
  }

  public get maxBuffer(): number {
    return this.manifest_.getMaxBuffer();
  }

  public get minBuffer(): number {
    return this.manifest_.getMinBuffer();
  }
  public get clockSyncUri(): string {
    return this.shakaPlayer_.getConfiguration().manifest.dash.clockSyncUri;
  }

  public get isLive(): boolean {
    return this.shakaPlayer_.isLive();
  }

  public get isMute(): boolean {
    return this.videoElement_.muted;
  }

  public get playbackOptions(): PlaybackOptions {
    return this.playbackOptions_;
  }

  public get volume(): number {
    return this.videoElement_.volume;
  }

  public set volume(volumeLevel: number) {
    this.videoElement_.volume = volumeLevel;
  }

  /**
   * Initializes preloading functionality
   */
  private initializePreload(): void {
    try {
      // Create preload manager using the existing Shaka Player instance
      this.preloadManager_ = new ShakaPreloadManager(
        this.shakaPlayer_,
        this.logger_,
        this.contentUrl_,
        this.mediaType_,
        this.playbackOptions_.initialPlaybackTime,
      );

      // Start preloading
      this.preloadManager_
        .preloadAsset()
        .then(() => {
          this.logger_.log('Asset successfully preloaded');
        })
        .catch((error: any) => {
          this.logger_.error('Failed to preload asset:', error);
          // Fall back to regular loading if preloading fails
          this.preloadManager_ = null;
        });
    } catch (error) {
      this.logger_.error('Error initializing preload:', error);
      this.preloadManager_ = null;
    }
  }

  /**
   * Loads the media content
   *
   * @param initialPlaybackTime - Initial time to start playback from (optional)
   * @returns Promise that resolves when loading is complete
   */
  public load(initialPlaybackTime?: number): Promise<void> {
    // Store initial playback time
    this.initialPlaybackTime_ = initialPlaybackTime;

    return this.loadAsset();
  }

  /**
   * load Asset
   * @private
   * @param initialPlaybackTime
   * @returns Promise<void>
   */
  private loadAsset(): Promise<void> {
    try {
      return this.shakaPlayer_
        .attach(this.videoElement_)
        .then(() => this.getLoadSource())
        .then(() => {
          this.isPlayingInLive_ = this.shakaPlayer_.isLive();
        })
        .then(() => this.sidecarLoadTextTracks_(this.vttTracks_))
        .then(() => this.setThumbnailTrack_())
        .then(() => {
          // Only play automatically if we're using preloaded content
          if (this.enablePreload && this.preloadManager_) {
            this.play();
          }
        })
        .catch((error: any) => {
          this.onError_({ detail: error });
        });
    } catch (error) {
      this.onError_({ detail: error });
      return Promise.reject(error);
    }
  }

  /**
   * Determines which content source to load based on preload settings
   * This function handles the logic for choosing between direct content loading
   * and preloaded asset loading based on the current configuration.
   *
   * @private
   * @returns Promise<void> A promise that resolves when content is loaded
   */
  private getLoadSource(): Promise<void> {
    if (!this.enablePreload || !this.preloadManager_) {
      return this.shakaPlayer_.load(this.contentUrl_, this.initialPlaybackTime_, getMimeType(this.mediaType_));
    } else {
      return this.shakaPlayer_.load(this.preloadManager_.getPreloadedAsset());
    }
  }

  public play(): Promise<void> {
    return this.videoElement_.play();
  }

  public pause(): void {
    this.videoElement_.pause();
  }

  public seek(seekPosition: number): void {
    let playTime = seekPosition;
    if (this.isLive) {
      playTime = seekPosition - this.getPresentationStartTime_();
      // Ensure the `seekPosition` is always within the seekable range.
      // We also add a configurable start offset since for a restart manifest (growing buffer),
      // the seek range start keeps fluctuating (due to dynamic `timeShiftBufferDefpth` in DASh manifest)
      const liveSeekStartOffset = this.playbackOptions_.liveSeekStartOffset || 2;
      const offset = this.isLive ? liveSeekStartOffset : 0;
      const seekStart = this.seekableRange().start - this.getPresentationStartTime_();
      const seekEnd = this.seekableRange().end - this.getPresentationStartTime_();

      // Note:
      // Clamp playTime with computed seekableRage rather than using shakaPlayer's actual seekRange inroder
      // to avoid seeking in the incorrect seekRange since shakaPlayer giving incorrect seekRange for growing manifest.
      playTime = Math.min(Math.max(seekStart + offset, playTime), seekEnd);
    }

    this.videoElement_.currentTime = playTime;
    this.videoElement_.removeEventListener('timeupdate', this.onTimeUpdate_);
  }

  public seekToLiveEdge(): void {
    if (this.isLive) {
      // Contents from few Encoders returns the current time of video in seconds instead of epoch.
      // It has to be converted to epoch using getPresentationStartTime_()
      this.videoElement_.currentTime = this.getLiveEdge() - this.getPresentationStartTime_();
    }
  }

  public stop(): void {
    // update live playback state based on the current Shaka player state for subsequent playbacks
    this.isPlayingInLive_ = this.shakaPlayer_.isLive();
    this.videoElement_.pause();
  }

  public muteOrUnmute(): void {
    this.videoElement_.muted = !this.isMute;
  }

  public subscribe(eventName: string, callback: AnyFunc): void {
    this.emitter_.subscribe(eventName, callback);
  }

  public unsubscribe(eventName: string, callback: AnyFunc): void {
    this.emitter_.unsubscribe(eventName, callback);
  }

  private createShakaPlayer = (): any => {
    const shakaPlayer = new this.shakaLibrary.Player();

    this.logger_.debug(`Using shaka player version: ${this.shakaLibrary.Player.version}`);

    // Note: The constructor no longer takes mediaElement as a parameter; use the attach method to attach
    // to a media element instead. (Deprecated in v4.6)

    return shakaPlayer;
  };

  private createManifest_ = (): Manifest => {
    return new ShakaManifestAdapter(this.shakaPlayer_, this.logger_);
  };

  private sidecarLoadTextTracks_ = async (vttTracks: SidecarTrack[] = []): Promise<void> => {
    const sidecarKind = (track: SidecarTrack): 'subtitles' | 'captions' =>
      track.type?.toLowerCase() === TrackKind.SUBTITLE ? 'subtitles' : 'captions';

    const loadedKeys = new Set<string>();
    const tracksToLoad = vttTracks.filter(track => {
      const key = `${sidecarKind(track)}:${track.languageCode}`;
      if (loadedKeys.has(key)) {
        return false;
      }
      loadedKeys.add(key);
      return true;
    });

    const loadedTracks: any[] = await Promise.all(
      tracksToLoad.map(track =>
        this.shakaPlayer_.addTextTrackAsync(track.url, track.languageCode, sidecarKind(track), 'text/vtt'),
      ),
    );

    const preferredTextLanguage = this.playbackOptions_.preferredTextLanguage;
    if (!preferredTextLanguage || !tracksToLoad.some(track => track.languageCode === preferredTextLanguage)) {
      return;
    }

    const preferredSidecarTrack = loadedTracks.find(track => track?.language === preferredTextLanguage);
    if (preferredSidecarTrack && typeof this.shakaPlayer_.selectTextTrack === 'function') {
      this.shakaPlayer_.selectTextTrack(preferredSidecarTrack);
      this.shakaPlayer_.setTextTrackVisibility(true);
      return;
    }

    this.manifest_.selectTrack({ type: TrackType.TEXT, language: preferredTextLanguage });
  };

  public async unload(): Promise<void> {
    this.logger_.log('Destroying player:', this);
    this.isUnloaded_ = true;
    this.removeShakaEventListeners_();
    this.removeVideoEventListeners();
    this.emitter_.reset();
    // Clean up timeout controller to prevent lingering timers
    if (this.timeoutController) {
      this.timeoutController.clear();
    }
    // Clear preloaded instance
    if (this.preloadManager_ && this.preloadManager_.hasPreloadedAsset()) {
      this.preloadManager_.clearPreloadedAsset();
      this.preloadManager_ = null;
    }
    // NOTE: shaka.Player.destroy() can hang indefinitely when called during a manifest
    // network error. This happens because Shaka wraps in-flight network requests
    // as notAbortable operations — destroy() waits for them to settle but if the
    // manifest URL is unreachable (e.g. network error triggering retryStreaming),
    // that request never completes and destroy() never resolves.
    //
    // getNetworkingEngine().destroy() does not help because the request was already
    // queued as notAbortable before the engine is torn down.
    //
    // Race against a timeout to guarantee unload() always resolves, ensuring
    // corePlayer.stop()'s finally() block (eventEmitter.reset, playerAdapter=null)
    // always runs on the first destroyPlayer() call.
    this.shakaPlayer_.getNetworkingEngine()?.destroy();
    let destroyTimeoutHandle: ReturnType<typeof setTimeout> | null = null;
    const destroyPromise = this.shakaPlayer_
      .destroy()
      .catch((error: any) => {
        this.logger_.warn('shakaPlayer_.destroy() rejected:', error);
      })
      .finally(() => {
        if (destroyTimeoutHandle !== null) {
          clearTimeout(destroyTimeoutHandle);
          destroyTimeoutHandle = null;
        }
      });
    const destroyTimeoutPromise = new Promise<void>(resolve => {
      destroyTimeoutHandle = setTimeout(resolve, DESTROY_TIMEOUT_MS);
    });
    await Promise.race([destroyPromise, destroyTimeoutPromise]);
  }

  public getAllTracks(): TrackInfo[] {
    return this.manifest_.getAllTracks();
  }

  public getActiveTracks(): TrackInfo[] {
    return this.manifest_.getActiveTracks();
  }

  public availableMimeTypes(trackType: TrackType): Array<string> {
    if (trackType === TrackType.AUDIO) {
      return Array.from(this.manifest_.getAvailableAudioCodecs());
    }
    // Note: Currently we have no support for available mime types for video tracks.
    return [];
  }

  public async unloadAndSavePreload(): Promise<void> {
    try {
      this.pause();
      /**
       * Detaches videoElement from player player and stores the current preloaded instance.
       * Source: https://github.com/shaka-project/shaka-player/issues/2792
       *
       * @param {boolean} keepAdManager
       * - Set to `false` to avoid retaining the ad manager.
       * Since Shaka Ad Manager is not used in our implementation, this should be `false`.
       *
       * @param {boolean} keepPlayheadPosition
       * Set to `true` to retain the current playhead position
       * in the preloaded instance for resuming playback later.
       */
      const savedPreloadInstance = await this.shakaPlayer_.detachAndSavePreload(false, true);
      return this.preloadManager_.setPreloadedAsset(savedPreloadInstance);
    } catch (error) {
      this.logger_.error('Error while unloading the player instance:', error);
      return Promise.reject(error);
    }
  }

  public restoreFromPreload(): void {
    // Dev Note: This player.load() method is invoked using the core Player instance itself,
    // ensuring that the playback lifecycle is managed consistently across all use cases.
    //no-op
  }

  public setPreferredMimeTypes(_trackType: TrackType, _mimeTypes: Array<string>): void {
    // no-op
  }

  public async getThumbnail(position: number): Promise<ThumbnailPreviewMetadata | undefined> {
    const thumbnailTrack = this.thumbanailTrack_ ?? this.shakaPlayer_.getImageTracks()[0];
    if (!thumbnailTrack) {
      return undefined;
    }
    const thumbnailMetadata = (await this.shakaPlayer_.getThumbnails(thumbnailTrack.id, position)) ?? undefined;
    if (thumbnailMetadata == null || thumbnailMetadata?.uris.length === 0) {
      return undefined;
    }

    const { imageWidth, imageHeight, width, height, positionX, positionY } = thumbnailMetadata;
    const crop = width > 0 && height > 0 ? { x: positionX, y: positionY, width, height } : undefined;
    const hasImageDimensions = imageWidth > 0 && imageHeight > 0 && width > 0 && height > 0;
    const isSprite = hasImageDimensions && (imageWidth > width || imageHeight > height);

    return {
      thumbnailRowIndex: isSprite ? 0 - positionX / width : 0,
      thumbnailColumnIndex: isSprite ? 0 - positionY / height : 0,
      totalRows: isSprite ? imageHeight / height : 0,
      totalColumns: isSprite ? imageWidth / width : 0,
      urls: thumbnailMetadata.uris,
      isSprite,
      ...(crop ? { crop } : {}),
    };
  }

  public getVariantTracks(): TrackVariantInfo[] {
    return this.shakaPlayer_.getVariantTracks().map((track: any) => ({
      id: track.id,
      type: track.type,
      language: track.language,
      mimeType: track.mimeType,
      bitrate: track.bandwidth,
      channelsCount: track.channelsCount,
      codecs: track.codecs,
      sampleRate: track.audioSamplingRate,
      width: track.width,
      height: track.height,
      frameRate: track.frameRate,
      spatialAudio: track.spatialAudio,
      hdr: track.hdr,
      pixelAspectRatio: track.pixelAspectRatio,
      audioRoles: track.audioRoles,
    }));
  }

  /**
   * Retrieves all available track variants for the specified track type.
   * This method filters out tracks with incomplete information and formats
   * the data according to the TrackVariantInfo interface.
   *
   * @param trackType - The type of track to retrieve (only VIDEO is currently supported)
   * @returns An array of track variant information objects
   */
  public getAvailableTrackVariants(trackType: TrackType): TrackVariantInfo[] {
    if (trackType !== TrackType.VIDEO) {
      return [];
    }

    const availableTracks: TrackVariantInfo[] = [];

    for (const availableVariantTrack of this.shakaPlayer_.getVariantTracks()) {
      if (
        availableVariantTrack.width === undefined ||
        availableVariantTrack.height === undefined ||
        availableVariantTrack.videoBandwidth === undefined
      ) {
        // Skip this track as it doesn't contain valid video track information
        continue;
      }

      const { width, height, videoBandwidth, codecs, mimeType } = availableVariantTrack;

      availableTracks.push({
        width,
        height,
        codecs, // This should match your interface property name
        mimeType,
        bandwidth: videoBandwidth,
      });
    }

    return availableTracks;
  }

  public selectTrack(track?: TrackInfo): void {
    this.manifest_.selectTrack(track);
  }

  public get playbackStatistics(): PlaybackStatistics {
    const shakaStats = this.shakaPlayer_.getStats();
    const buffered = this.buffered;

    const lastPlayedVariant = shakaStats.switchHistory?.length
      ? shakaStats.switchHistory[shakaStats.switchHistory.length - 1]
      : undefined;

    const activeTrack = this.getActiveTrack();
    return Object.freeze({
      bufferRange: `- ${buffered.behind.toFixed(0)}s / + ${buffered.ahead.toFixed(0)}s`,
      bufferedAhead: buffered.ahead,
      decodedFrames: shakaStats.decodedFrames,
      droppedFrames: shakaStats.droppedFrames,
      corruptedFrames: shakaStats.corruptedFrames,
      playTime: shakaStats.playTime.toFixed(2),
      pauseTime: shakaStats.pauseTime.toFixed(2),
      licenseTime: shakaStats.licenseTime,
      bufferingTime: shakaStats.bufferingTime.toFixed(2),
      requiredBandwidth: Math.round(shakaStats.streamBandwidth),
      estimatedBandwidth: Math.round(shakaStats.estimatedBandwidth),
      variantBitrate: activeTrack ? activeTrack.bandwidth : 0,
      videoBitrate: activeTrack ? activeTrack.videoBandwidth : 0,
      audioBitrate: activeTrack ? activeTrack.audioBandwidth : 0,
      loadLatency: shakaStats.loadLatency.toFixed(2),
      width: shakaStats.width,
      height: shakaStats.height,
      framerate: activeTrack ? activeTrack.frameRate : 0,
      lastPlayedVariantBandwidth: lastPlayedVariant ? lastPlayedVariant.bandwidth : undefined,
    });
  }

  public setRequestInterceptor(interceptor: (requestType: RequestType, request: any) => void): void {
    this.requestInterceptor_ = interceptor;
  }

  public setResponseInterceptor(interceptor: (requestType: RequestType, response: any) => void): void {
    this.responseInterceptor_ = interceptor;
  }

  public getLiveEdge(): number {
    return this.seekableRange().end;
  }

  public seekableRange(): SeekRangeInfo {
    // Contents from few Encoders returns the current time of video in seconds instead of epoch.
    // It has to be converted to epoch using getPresentationStartTime_()
    if (this.isLive) {
      return {
        // Note:
        // By default, shakaPlayer calculate a presentation start based on explicit segment end times to account drift caused by encoders.
        // This computed presentation start time is few seconds either ahead or behind the actual availabilityStartTime. To solve incorrect presentation start time,
        // diabling autoCorrectDrift makes shakaPlayer to use availabilityStartTime as presenation start but this causes seekRange end to fall behind the seekStart resulting
        // in incorrect currentTime during clamping with seekRange for growing manifest if the suggestedPresentationDelay is applied since the liveEdge is closer to the seekRange start.
        // Refer: QPS-786 for full details.
        start: this.getPresentationStartTime_() + this.shakaPlayer_.seekRange().start,
        end:
          this.getPresentationStartTime_() +
          // If seekRange end falls behind seekRange start, use currenTime as seekRangeEnd.
          (this.shakaPlayer_.seekRange().end < this.shakaPlayer_.seekRange().start
            ? this.videoElement_.currentTime
            : this.shakaPlayer_.seekRange().end),
      };
    }
    return this.shakaPlayer_.seekRange();
  }

  public currentOffsetFromLiveEdge(): number {
    let offset = 0;
    if (this.isLive) {
      // Note:
      // Contents from few Encoders returns seekabale range endTime  behind the currentTime.
      // To handle this. If the seekable range endTime is behind the currentTime we are setting the offset value as 0.

      const seekRange = this.shakaPlayer_.seekRange();
      offset = this.videoElement_.currentTime < seekRange.end ? seekRange.end - this.videoElement_.currentTime : 0;
    }
    return offset;
  }

  public setPlaybackOptions(playbackOptions: PlaybackOptions): void {
    this.playbackOptions_ = playbackOptions;
  }

  private getPresentationStartTime_(): number {
    const presentationStartTime = this.shakaPlayer_.getPresentationStartTimeAsDate();
    return presentationStartTime ? presentationStartTime.getTime() / 1000 : 0;
  }

  private setNetworkInterceptors_(): void {
    this.shakaPlayer_.getNetworkingEngine().registerRequestFilter((requestType: RequestType, request: any) => {
      return this.requestProcessor_(requestType, request);
    });
    this.shakaPlayer_.getNetworkingEngine().registerResponseFilter((requestType: RequestType, response: any) => {
      return this.responseProcessor_(requestType, response);
    });
  }

  private requestProcessor_ = (requestType: RequestType, request: any): Promise<void> => {
    return Promise.resolve().then(() => {
      this.observeNetwork_('networkrequest', requestType);
      if (this.requestInterceptor_) return this.requestInterceptor_(requestType, request);
    });
  };

  private responseProcessor_ = (requestType: RequestType, response: any): void => {
    this.observeNetwork_('networkresponse', requestType, response);
    if (this.responseInterceptor_) this.responseInterceptor_(requestType, response);
  };

  private observeNetwork_(event: string, requestType: RequestType, response?: any): void {
    if ([RequestType.MANIFEST, RequestType.SEGMENT, RequestType.LICENSE].includes(requestType)) {
      const observation: NetworkRequestObservation = { requestType, timestamp: Date.now() };
      if (response) this.addNetworkResponseDetails_(observation, response);
      this.emitter_.publish(event, observation);
    }
  }

  private addNetworkResponseDetails_(observation: NetworkRequestObservation, response: any): void {
    const bytes = response.data?.byteLength;
    if (typeof bytes === 'number' && Number.isFinite(bytes) && bytes >= 0) observation.bytesTransferred = bytes;
    if (typeof response.timeMs === 'number' && Number.isFinite(response.timeMs) && response.timeMs > 0) {
      observation.durationMs = response.timeMs;
    }

    const contentType = this.getNetworkContentType_(response);
    if (contentType) observation.contentType = contentType;
  }

  private getNetworkContentType_(response: any): NetworkRequestObservation['contentType'] | undefined {
    const extension =
      typeof response.uri === 'string'
        ? response.uri
            .split(/[?#]/)[0]
            .match(/\.(mpd|m3u8|m4s|ts|mp4|aac|vtt)$/i)?.[1]
            .toLowerCase()
        : undefined;
    if (extension) return extension as NetworkRequestObservation['contentType'];

    const mimeTypes: Record<string, NonNullable<NetworkRequestObservation['contentType']>> = {
      'application/dash+xml': 'mpd',
      'application/vnd.apple.mpegurl': 'm3u8',
      'application/x-mpegurl': 'm3u8',
      'video/mp2t': 'ts',
      'video/mp4': 'mp4',
      'audio/mp4': 'mp4',
      'audio/aac': 'aac',
      'text/vtt': 'vtt',
    };
    const contentType = response.headers?.['content-type'];
    if (typeof contentType !== 'string') return undefined;
    const mime = contentType.split(';')[0].trim().toLowerCase();
    return mimeTypes[mime];
  }

  private getActiveTrack(): any {
    return this.shakaPlayer_.getVariantTracks().find((track: any) => track.active);
  }

  private setLoadConfigurations_(): void {
    const playbackOptions = this.playbackOptions_;
    this.initialPlaybackTime_ = playbackOptions.initialPlaybackTime;
    this.setMaxBuffer(playbackOptions.maxBuffer);
    this.setMinBuffer(playbackOptions.minBuffer);
    this.setPreferredTextLanguage();
    this.setPreferredVideoCodecs();
    this.setPreferredAudioCodecs();
    this.setPreferredKeySystems();
    this.setNetworkRetryConfigurations();
    this.setMediaSourceConfig(playbackOptions.forceTransmuxTS || false);
    this.setAbrConfigurations(playbackOptions);
    this.setMediaSourceConfig(playbackOptions.forceTransmuxTS || false);
    this.setStreamingConfigurations(playbackOptions);
    this.setAdvancedAbrConfiguration(playbackOptions);
    this.setAudioPreferences(playbackOptions);

    if (playbackOptions.ignoreSuggestedPresentationDelay) {
      this.manifest_.setIgnoreSuggestedPresentationDelay(playbackOptions.ignoreSuggestedPresentationDelay);
    }

    if (playbackOptions.segmentRelativeVttTiming) {
      this.manifest_.setSegmentRelativeVttTiming(playbackOptions.segmentRelativeVttTiming);
    }

    if (playbackOptions.defaultPresentationDelay) {
      this.manifest_.setDefaultPresentationDelay(playbackOptions.defaultPresentationDelay);
    }

    if (playbackOptions.bufferBehind) {
      this.manifest_.setBufferBehind(playbackOptions.bufferBehind);
    }

    if (playbackOptions.clockSyncUri) {
      this.manifest_.setClockSyncUri(playbackOptions.clockSyncUri);
    }

    if (playbackOptions.ignoreMinBufferTime) {
      this.manifest_.setIgnoreMinBufferTime(playbackOptions.ignoreMinBufferTime);
    }

    if (playbackOptions.autoCorrectDrift !== undefined) {
      this.manifest_.setAutoCorrectDrift(playbackOptions.autoCorrectDrift);
    }

    // DEPRECATED: This configuration is deprecated from Shaka Player version 4.11.0
    // Reference: https://shaka-player-demo.appspot.com/docs/api/tutorial-upgrade.html
    if (typeof playbackOptions.safariBehaviourForLiveHls === 'boolean' && !playbackOptions.safariBehaviourForLiveHls) {
      this.manifest_.setSafariBehaviourForLiveHls(playbackOptions.safariBehaviourForLiveHls);
    }

    // FEATURE: This configuration is available from Shaka Player version 4.16.3+
    this.manifest_.setEnableAudioGroups(playbackOptions?.enableAudioGroups ?? false);

    if (playbackOptions.hlsLiveSegmentsDelay) {
      this.manifest_.setHlsLiveSegmentsDelay(playbackOptions.hlsLiveSegmentsDelay);
    }

    if (typeof playbackOptions.disableCodecGuessingForHls === 'boolean') {
      this.manifest_.setDisableCodecGuessingForHls(playbackOptions.disableCodecGuessingForHls);
    }

    this.manifest_.setRaiseFatalErrorOnManifestUpdateRequestFailure(
      playbackOptions?.raiseFatalErrorOnManifestUpdateRequestFailure ?? false,
    );
  }

  private setMaxBuffer(maxBufferSeconds?: number): void {
    const minBuffer = this.manifest_.getMinBuffer();
    if (maxBufferSeconds && maxBufferSeconds > minBuffer) {
      this.manifest_.setMaxBuffer(maxBufferSeconds);
    } else {
      this.logger_.warn(`Max Buffer ${maxBufferSeconds} should be greater than Min Buffer ${minBuffer}`);
    }
  }

  private setAdvancedAbrConfiguration(playbackOptions: PlaybackOptions): void {
    const advAbrAttr: any = {};

    if (playbackOptions.minTotalBytes) {
      advAbrAttr['minTotalBytes'] = playbackOptions.minTotalBytes;
    }

    if (playbackOptions.minBytes) {
      advAbrAttr['minBytes'] = playbackOptions.minBytes;
    }

    if (playbackOptions.fastHalfLife) {
      advAbrAttr['fastHalfLife'] = playbackOptions.fastHalfLife;
    }

    if (playbackOptions.slowHalfLife) {
      advAbrAttr['slowHalfLife'] = playbackOptions.slowHalfLife;
    }

    this.shakaPlayer_.configure({
      abr: { advanced: advAbrAttr },
    });
  }

  private setAbrConfigurations(playbackOptions: PlaybackOptions): void {
    const abrConfig: string[] = [
      'preferredBitrate',
      'bandwidthUpgradeTarget',
      'bandwidthDowngradeTarget',
      'switchInterval',
    ];
    let key: keyof PlaybackOptions;
    const abrAttr: any = {};
    for (key in playbackOptions) {
      if (abrConfig.includes(key) && playbackOptions[key]) {
        abrAttr[key] = playbackOptions[key];
      }
    }

    if (abrAttr.hasOwnProperty('preferredBitrate')) {
      abrAttr['defaultBandwidthEstimate'] = abrAttr['preferredBitrate'];
      delete abrAttr['preferredBitrate'];
    }

    if (
      typeof this.playbackOptions_.useNetworkInformation === 'boolean' &&
      !this.playbackOptions_.useNetworkInformation
    ) {
      abrAttr['useNetworkInformation'] = false;
    }

    if (typeof playbackOptions.clearBufferSwitch === 'boolean') {
      abrAttr['clearBufferSwitch'] = playbackOptions.clearBufferSwitch;
    }

    if (typeof playbackOptions.safeMarginSwitch === 'number') {
      abrAttr['safeMarginSwitch'] = playbackOptions.safeMarginSwitch;
    }

    this.shakaPlayer_.configure({
      abr: abrAttr,
    });
  }

  private setMediaSourceConfig(forceTransmuxTS: boolean): void {
    if (forceTransmuxTS) {
      this.shakaPlayer_.configure({
        mediaSource: {
          forceTransmux: forceTransmuxTS,
        },
      });
    }
  }

  private async setThumbnailTrack_(): Promise<void> {
    if (!this.webVttThumbnailUrl_) {
      return;
    }
    this.thumbanailTrack_ = await this.shakaPlayer_.addThumbnailsTrack(this.webVttThumbnailUrl_);
  }

  public restrictQuality(enableRestriction = false): void {
    const abrAttr: any = {};
    abrAttr['restrictToElementSize'] = enableRestriction;
    abrAttr['restrictToScreenSize'] = enableRestriction;
    this.logger_.debug(`Updating Abr restrictions: ${abrAttr}`);

    this.shakaPlayer_.configure({
      abr: abrAttr,
    });
  }

  private setNetworkRetryConfigurations(): void {
    if (!this.playbackOptions) {
      return;
    }

    const networkRetryConfig: any = {
      retryParameters: {
        timeout: this.processNetworkTimeouts(this.playbackOptions.networkTimeout),
        stallTimeout: this.processNetworkTimeouts(this.playbackOptions.stallTimeout, DEFAULT_STALL_TIMEOUT_MS),
        connectionTimeout: this.processNetworkTimeouts(
          this.playbackOptions.connectionTimeout,
          DEFAULT_CONNECTION_TIMEOUT_MS,
        ),
        baseDelay: this.processNetworkTimeouts(this.playbackOptions.retryDelay),
        maxAttempts: this.playbackOptions.networkRetries ? this.playbackOptions.networkRetries + 1 : undefined,
      },
    };

    this.shakaPlayer_.configure({
      drm: networkRetryConfig,
      manifest: networkRetryConfig,
      streaming: networkRetryConfig,
    });
  }

  private setStreamingConfigurations(playbackOptions: PlaybackOptions): void {
    const streamingAttr: any = {};
    if (typeof playbackOptions.stallSkip === 'number') {
      streamingAttr['stallSkip'] = playbackOptions.stallSkip;
    }
    if (typeof playbackOptions.stallThreshold === 'number') {
      streamingAttr['stallThreshold'] = playbackOptions.stallThreshold;
    }
    if (typeof playbackOptions.stallEnabled === 'boolean') {
      streamingAttr['stallEnabled'] = playbackOptions.stallEnabled;
    }
    if (typeof playbackOptions.inaccurateManifestTolerance === 'number') {
      streamingAttr['inaccurateManifestTolerance'] = playbackOptions.inaccurateManifestTolerance;
    }
    if (typeof playbackOptions.lowLatencyMode === 'boolean') {
      streamingAttr['lowLatencyMode'] = playbackOptions.lowLatencyMode;
    }

    // Note: autoLowLatencyMode configuration has been deprecated and will be removed in v5.0 .
    // We are currently at version v4.14.  Please Use streaming.lowLatencyMode
    // if the stream is low latency, this will be auto applied.
    if (typeof playbackOptions.autoLowLatencyMode === 'boolean') {
      streamingAttr['autoLowLatencyMode'] = playbackOptions.autoLowLatencyMode;
    }
    if (typeof playbackOptions.useNativeHlsForFairPlay === 'boolean') {
      streamingAttr['useNativeHlsForFairPlay'] = playbackOptions.useNativeHlsForFairPlay;
    }

    if (typeof playbackOptions.segmentPrefetchLimit === 'number') {
      this.logger_.log('setting prefetch limit to', playbackOptions.segmentPrefetchLimit);
      streamingAttr['segmentPrefetchLimit'] = playbackOptions.segmentPrefetchLimit;
    }

    if (typeof playbackOptions.startAtLiveEdge === 'boolean') {
      streamingAttr['returnToEndOfLiveWindowWhenOutside'] = playbackOptions?.startAtLiveEdge;
    }

    // Maximum gap (in seconds) that the player will automatically jump over.
    // Shaka default is 0.5s. Only overridden when explicitly set in PlaybackOptions.
    if (typeof playbackOptions.gapDetectionThreshold === 'number') {
      streamingAttr['gapDetectionThreshold'] = playbackOptions.gapDetectionThreshold;
    }

    // Controls MSE reset behavior when crossing period boundaries (e.g. encrypted content transitions).
    // Platform-specific Shaka defaults apply unless explicitly set: KEEP (most platforms),
    // RESET_TO_ENCRYPTED (Tizen 3), RESET (webOS 3).
    if (typeof playbackOptions.crossBoundaryStrategy === 'string') {
      streamingAttr['crossBoundaryStrategy'] = playbackOptions.crossBoundaryStrategy;
    }

    this.shakaPlayer_.configure({
      streaming: streamingAttr,
    });
  }

  private processNetworkTimeouts(
    timerValue: number | undefined,
    defaultValue: number | undefined = undefined,
  ): number | undefined {
    return typeof timerValue === 'number' ? timerValue * 1000 : defaultValue;
  }

  private setMinBuffer(minBufferSeconds?: number): void {
    const maxBuffer = this.manifest_.getMaxBuffer();
    if (minBufferSeconds && maxBuffer > minBufferSeconds) {
      this.manifest_.setMinBuffer(minBufferSeconds);
    } else {
      this.logger_.warn(`Min Buffer ${minBufferSeconds} should be less than Max Buffer ${maxBuffer}`);
    }
  }

  /**
   * Sets the preferred text language for playback.
   */
  private setPreferredTextLanguage(): void {
    if (this.playbackOptions_.preferredTextLanguage) {
      this.shakaPlayer_.configure({
        preferredTextLanguage: this.playbackOptions_.preferredTextLanguage,
      });
    }
  }

  private setPreferredVideoCodecs(): void {
    if (this.playbackOptions_.preferredVideoCodecs && this.playbackOptions_.preferredVideoCodecs.length > 0) {
      this.shakaPlayer_.configure({
        preferredVideoCodecs: this.playbackOptions_.preferredVideoCodecs,
      });
    }
  }

  /**
   * Set preferred audio codecs based on priority and availability
   * If no preferred audio codecs are specified, player will default to Dolby Atmos (eac3-joc) with spatial audio enabled
   * @private setPreferredAudioCodecs
   * @returns {void}
   */
  private setPreferredAudioCodecs(): void {
    const { preferredAudioCodecs } = this.playbackOptions_;

    const config: { preferSpatialAudio: boolean; preferredAudioCodecs?: string | string[] } = {
      preferSpatialAudio: false,
    };

    // Default to Dolby Atmos (eac3-joc) if no preferred audio codecs are specified
    const codecPreference = preferredAudioCodecs?.length ? preferredAudioCodecs[0] : 'eac3-joc';

    // Use a switch statement to handle different codecs
    switch (codecPreference) {
      case 'eac3-joc':
        // Enable spatial audio for Dolby Atmos (default)
        config.preferredAudioCodecs = ['ec-3'];
        config.preferSpatialAudio = true;
        this.logger_.debug('Enabling spatial audio for Dolby Atmos (default)');
        break;
      case 'eac3':
        // Handle standard EAC3 codec
        config.preferredAudioCodecs = ['ec-3'];
        break;
      default:
        // Use the provided codecs as-is
        config.preferredAudioCodecs = preferredAudioCodecs;
        break;
    }

    // Apply the configuration
    this.shakaPlayer_.configure(config);
    this.logger_.debug('Using codec preferences:', config);
  }

  private setPreferredKeySystems(): void {
    if (this.playbackOptions_.preferredKeySystems && this.playbackOptions_.preferredKeySystems.length > 0) {
      this.shakaPlayer_.configure({
        drm: {
          preferredKeySystems: this.playbackOptions_.preferredKeySystems,
        },
      });
    }
  }

  /**
   * Sets all audio-related preferences in a single configuration call.
   *
   * Configuration:
   * - preferredAudioLanguage: The preferred audio language for playback
   * - preferredAudioRole: The preferred audio role (e.g., 'main', 'commentary')
   * - ignoreCodecFiltering: Default is true (don't filter codecs, keep all variants).
   *   If user explicitly sets true/false, we honor that.
   * - preferredAudioChannelCount: Default is 16 (prefer highest quality audio).
   *   If user explicitly sets a value, we honor that.
   *
   * @param playbackOptions - PlaybackOptions containing audio preferences
   */
  private setAudioPreferences(playbackOptions: PlaybackOptions): void {
    // Default ignoreCodecFiltering to true (Quickplay default), honor user's explicit setting
    const ignoreCodecFiltering = playbackOptions?.ignoreCodecFiltering ?? true;

    // Default preferredAudioChannelCount to 16 (max quality), honor user's explicit setting
    const preferredAudioChannelCount = playbackOptions?.preferredAudioChannelCount ?? 16;

    // Get audio language and role preferences
    const preferredAudioLanguage = playbackOptions?.preferredAudioLanguage || '';
    const preferredAudioRole = playbackOptions?.preferredAudioRole || '';

    this.logger_.debug(
      `Setting audio preferences: language=${preferredAudioLanguage}, role=${preferredAudioRole}, ` +
        `ignoreCodecFiltering=${ignoreCodecFiltering}, preferredAudioChannelCount=${preferredAudioChannelCount}`,
    );

    this.shakaPlayer_.configure({
      preferredAudioLanguage: preferredAudioLanguage,
      preferredAudioRole: preferredAudioRole,
      preferredAudioChannelCount: preferredAudioChannelCount,
      streaming: {
        dontChooseCodecs: ignoreCodecFiltering,
      },
    });
  }

  private addVideoEventListeners = (): void => {
    this.videoElement_.addEventListener('seeked', this.onSeeked_);
    this.videoElement_.addEventListener('pause', this.onPause_);
    this.videoElement_.addEventListener('play', this.onPlay_);
    this.videoElement_.addEventListener('ended', this.onEnded_);

    // We observed a race-condition where timeupdate is triggered earlier than canplay on Safari browser when playing an SSAI enabled live stream in restart mode.
    // In such cases, the player publishes an invalid epoch time for a brief moment to subscribed component(e.g., Heartbeat) which causes some issues.
    // Refer: FLPS-527 for full details.
    if (!PlatformUtil.isApple()) {
      this.videoElement_.addEventListener('timeupdate', this.onTimeUpdate_);
    }
    // The spec says that duration information is supposed to be available
    // on `loadedmetadata`, which would indicate that we can set currentTime
    // at that point. But in Safari this is not the case. Anywhere where we
    // would normally listen for `loadedmetadata`, we have to listen for
    // `loadeddata`, `canplay` instead.
    // As shaka uses `loadedData` setting video current time fails due to race condition
    // Hence in our library we are using `canplay`.
    this.videoElement_.addEventListener('canplay', this.onCanPlay_);
    if (PlatformUtil.isApple()) {
      // Shaka player also listens to video element error
      // We should use the error from Shaka instead of listening to the video element directly
      // We attach this only for Safari since we are using src mode playback
      this.videoElement_.addEventListener('error', this.onVideoError_);
    }
    this.videoElement_.addEventListener('volumechange', this.onVolumeChanged_);
  };

  private removeVideoEventListeners = (): void => {
    this.videoElement_.removeEventListener('seeked', this.onSeeked_);
    this.videoElement_.removeEventListener('pause', this.onPause_);
    this.videoElement_.removeEventListener('play', this.onPlay_);
    this.videoElement_.removeEventListener('ended', this.onEnded_);
    this.videoElement_.removeEventListener('timeupdate', this.onTimeUpdate_);
    this.videoElement_.removeEventListener('canplay', this.onCanPlay_);

    if (PlatformUtil.isApple()) {
      this.videoElement_.removeEventListener('error', this.onVideoError_);
    }
    this.videoElement_.removeEventListener('volumechange', this.onVolumeChanged_);
  };

  private addShakaEventListeners_ = (): void => {
    this.shakaPlayer_.addEventListener('error', this.onError_);
    this.shakaPlayer_.addEventListener('buffering', this.onBuffering_);
    this.shakaPlayer_.addEventListener('trackschanged', this.onTracksChanged_);
    this.shakaPlayer_.addEventListener('audiotrackchanged', this.onAudioTrackChanged_);
    this.shakaPlayer_.addEventListener('textchanged', this.onTextChanged_);
    this.shakaPlayer_.addEventListener('drmsessionupdate', this.onDrmSessionUpdate_);
    this.shakaPlayer_.addEventListener('emsg', this.onEmsgEvent_);
    this.shakaPlayer_.addEventListener('streaming', this.onStreamingEvent_);
    this.shakaPlayer_.addEventListener('adaptation', this.onAdaptation_);
    this.shakaPlayer_.addEventListener('metadata', this.onMetadata_);
    this.shakaPlayer_.addEventListener('ratechange', this.onRateChanged_);
    this.shakaPlayer_.addEventListener('manifestparsed', this.onManifestparsed_);
    this.shakaPlayer_.addEventListener('timelineregionenter', this.onTimelineRegionEnter_);
    this.shakaPlayer_.addEventListener('audiotrackschanged', this.onAudioTracksChanged_);
    this.shakaPlayer_.addEventListener('manifestupdated', this.onManifestUpdated_);
    this.shakaPlayer_.addEventListener('variantchanged', this.onVariantChanged_);
    this.shakaPlayer_.getNetworkingEngine().addEventListener('retry', this.manifestErrorHandler);
  };

  private removeShakaEventListeners_ = (): void => {
    this.shakaPlayer_.removeEventListener('error', this.onError_);
    this.shakaPlayer_.removeEventListener('buffering', this.onBuffering_);
    this.shakaPlayer_.removeEventListener('trackschanged', this.onTracksChanged_);
    this.shakaPlayer_.removeEventListener('audiotrackchanged', this.onAudioTrackChanged_);
    this.shakaPlayer_.removeEventListener('textchanged', this.onTextChanged_);
    this.shakaPlayer_.removeEventListener('drmsessionupdate', this.onDrmSessionUpdate_);
    this.shakaPlayer_.removeEventListener('emsg', this.onEmsgEvent_);
    this.shakaPlayer_.removeEventListener('streaming', this.onStreamingEvent_);
    this.shakaPlayer_.removeEventListener('adaptation', this.onAdaptation_);
    this.shakaPlayer_.removeEventListener('metadata', this.onMetadata_);
    this.shakaPlayer_.removeEventListener('ratechange', this.onRateChanged_);
    this.shakaPlayer_.removeEventListener('manifestparsed', this.onManifestparsed_);
    this.shakaPlayer_.removeEventListener('timelineregionenter', this.onTimelineRegionEnter_);
    this.shakaPlayer_.removeEventListener('manifestupdated', this.onManifestUpdated_);
    this.shakaPlayer_.removeEventListener('audiotrackschanged', this.onAudioTracksChanged_);
    this.shakaPlayer_.removeEventListener('variantchanged', this.onVariantChanged_);
    this.shakaPlayer_.getNetworkingEngine()?.removeEventListener('retry', this.manifestErrorHandler);
  };

  /**
   * Handles Shaka VariantChangedEvent.
   *
   * Triggered when a manual variant change occurs
   * (selectAudioTrack, selectAudioLanguage, selectVariantTrack).
   * Since audio is tied to variants, we detect the active audio track
   * and emit a custom 'audiotrackchanged' event for the app.
   *
   * Note: Not triggered for automatic ABR switches.
   */
  private readonly onVariantChanged_ = (event: any): void => {
    const { oldTrack, newTrack } = event;
    if (!oldTrack || !newTrack) return;

    const audioChanged =
      oldTrack.language !== newTrack.language ||
      JSON.stringify(oldTrack.audioRoles) !== JSON.stringify(newTrack.audioRoles);

    if (audioChanged) {
      this.logger_.debug(
        'Audio track changed via VariantChangedEvent:',
        `language=${newTrack.language}, label=${newTrack.label}, role=${newTrack.roles?.[0]}`,
      );

      this.emitter_.publish('audiotrackchanged');
    }
  };

  private onBufferError_ = (): Error => {
    return createError(
      PlayerErrorCodes.BUFFER_TIMEOUT | ErrorCodes.ERROR_CATEGORY_MASK_PLAYBACK,
      'Buffer Timeout',
      undefined,
    );
  };

  private onManifestparsed_ = (): void => {
    // A manifest was successfully parsed, so any prior failed retry attempts no longer apply.
    this.manifestRetryAttemptCount_ = 0;
    this.emitter_.publish('manifestparsed');
    if (this.isLive && this.initialPlaybackTime_ && !PlatformUtil.isApple()) {
      let playTime = this.initialPlaybackTime_;
      playTime = this.initialPlaybackTime_ - this.getPresentationStartTime_();
      // In some encoders, presentationStartTime will be ahead of initialPlaybackTime for restart playback.
      // In such cases, player will fall to live edge due to negative updated startTime after
      // computing the actual startTime using initialPlaybackTime and presentationStartTime.
      // To handle this, We should always wanted to verify that startTime remains non-negative.
      playTime = Math.max(0, playTime);
      this.shakaPlayer_.updateStartTime(playTime);
    }
  };

  private onCanPlay_ = (): void => {
    if (PlatformUtil.isApple()) {
      this.videoElement_.addEventListener('timeupdate', this.onTimeUpdate_);
    }
  };

  private onVolumeChanged_ = (_event: any): void => {
    this.emitter_.publish('volumechanged');
  };

  private readonly onError_ = async (error: any): Promise<void> => {
    const shakaError = error.detail;
    //dev note: Player will be retrying infinite times when shaka throws HTTP_ERROR(1002) during network interruption.
    // This will reduce the multiple occurrence of 410201 error and resumes the stream seamlessly when network back to online.

    if (
      this.playbackOptions_.infiniteRetriesOnNetworkError &&
      shakaError &&
      shakaError.code === this.shakaLibrary.util.Error.Code.HTTP_ERROR
    ) {
      this.shakaPlayer_.retryStreaming();
      return;
    }

    // Attempt seamless recovery for EXPIRED (6014).
    // This fires when the DRM license expires (e.g. after 12 hours) and Shaka's
    // auto-renewal request fails because the original access token is also expired.
    if (shakaError && shakaError.code === this.shakaLibrary.util.Error.Code.EXPIRED) {
      const recovered = await this.recoverExpiredDrmLicense_();
      if (recovered) return;
    }

    if (shakaError && shakaError.severity === this.shakaLibrary.util.Error.Severity.CRITICAL) {
      const playerError: Error = convertToPlayerError(error, this.shakaLibrary.util.Error);
      this.emitter_.publish('error', playerError);
    } else {
      this.logger_.warn(`An error occurred, but the player is attempting to recover from the error`, error);
    }
  };

  /**
   * Attempts seamless DRM license recovery when Shaka reports `EXPIRED` (6014).
   *
   * Returns `true` if recovery succeeded and the error should be suppressed,
   * or `false` if recovery was skipped or failed so the caller can surface the
   * error through the normal severity-based path.
   */
  private readonly recoverExpiredDrmLicense_ = async (): Promise<boolean> => {
    if (this.isRecoveringLicense_) {
      // A recovery attempt is already in flight — swallow duplicate events.
      this.logger_.warn('DRM license recovery already in progress, ignoring duplicate 6014');
      return true;
    }
    if (this.isUnloaded_) return false;
    const callback = this.playbackOptions_.onDrmLicenseExpired;
    if (callback && this.licenseRenewalAttempts_ < ShakaPlayerAdapter.MAX_LICENSE_RENEWAL_ATTEMPTS) {
      this.isRecoveringLicense_ = true;
      this.licenseRenewalAttempts_++;
      try {
        const freshLicenseUrl = await callback();
        if (this.isUnloaded_) return false;
        if (freshLicenseUrl) {
          const keySystem = getKeySystemForDrmScheme(this.drmScheme_ ?? DrmScheme.WIDEVINE);
          this.shakaPlayer_.configure({ drm: { servers: { [keySystem]: freshLicenseUrl } } });
          this.licenseUrl_ = freshLicenseUrl;
          this.shakaPlayer_.retryStreaming();
          this.logger_.log('DRM license expired — recovered with fresh license URL');
          return true;
        }
      } catch (e) {
        this.logger_.warn('DRM license recovery callback failed', e);
      } finally {
        this.isRecoveringLicense_ = false;
      }
    }
    return false;
  };

  private onMetadata_ = (event: any): void => {
    const eventMetadata = Object.assign(event, { type: 'metadata' });
    this.emitter_.publish('timedmetadata', eventMetadata);
  };

  private readonly onAudioTracksChanged_ = (event: any): void => {
    this.logger_.log('audio tracks have changed', event);
    // Publish an event to notify listeners that audio tracks have changed
    this.emitter_.publish('audiotrackschanged', event);
  };

  private onTimelineRegionEnter_ = (event: any): void => {
    const eventMetadata = Object.assign(event, { type: 'timelineregionenter' });
    this.emitter_.publish('daitimelineregionenter', eventMetadata);
  };

  /**
   * Checks whether the player has transitioned from a LIVE stream to a VOD stream.
   *
   * This can happen in live streams where the manifest switches from
   * a dynamic (live) presentation to a static (VOD) presentation after the broadcast ends.
   *
   * @returns `true` if the player was previously playing a live stream and Shaka now
   * reports the stream as non-live (i.e., a live-to-VOD transition has occurred).
   */
  private readonly isPlayerTransitionedFromLiveToVOD = (): boolean => {
    return this.isPlayingInLive_ && !this.shakaPlayer_.isLive();
  };

  /**
   * Validates whether a live-to-VOD mode switch has occurred and, if so, updates
   * internal state and notifies subscribers.
   *
   * Called on every manifest update to detect when a live stream ends and the
   * manifest transitions to a static/VOD presentation. Publishes the
   * `livetovodtransition` event so consumers can adjust UI or playback behaviour
   * accordingly.
   */
  private readonly validateModeSwitched = (): void => {
    if (!this.isPlayerTransitionedFromLiveToVOD()) {
      return;
    }
    this.logger_.log('Player switched from LIVE to VOD');
    this.isPlayingInLive_ = this.shakaPlayer_.isLive();
    this.emitter_.publish('livetovodtransition');
  };

  private onManifestUpdated_ = (): void => {
    // A live manifest update succeeded, so any prior failed retry attempts no longer apply.
    this.manifestRetryAttemptCount_ = 0;
    this.validateModeSwitched();
    this.emitter_.publish('manifestupdated');
  };

  private onAdaptation_ = (): void => {
    this.emitter_.publish('bandwidthchanged');
  };

  private onStreamingEvent_ = (): void => {
    this.emitter_.publish('trackavailabilitychanged');
  };

  private onTracksChanged_ = (): void => {
    this.emitter_.publish('trackschanged');
  };

  private readonly onTextChanged_ = (): void => {
    this.emitter_.publish('textchanged');
  };

  private readonly onAudioTrackChanged_ = (): void => {
    this.emitter_.publish('audiotrackchanged');
  };

  private onRateChanged_ = (): void => {
    this.emitter_.publish('ratechanged');
  };

  private onDrmSessionUpdate_ = (): void => {
    this.licenseRenewalAttempts_ = 0;
    this.emitter_.publish('drmsessionupdate');
  };

  private onBuffering_ = (event: any): void => {
    const eventName = event.buffering ? 'bufferingstart' : 'bufferingend';
    this.emitter_.publish(eventName);
    // Note: If the bufferTimeoutMs is defined only we used to set the safeTimeout
    if (this.playbackOptions_.bufferTimeoutMs != undefined && eventName == 'bufferingstart') {
      // Set a timeout
      this.timeoutController.set(() => {
        this.emitter_.publish('buffererror', this.onBufferError_());
      }, this.playbackOptions_.bufferTimeoutMs);
    } else {
      // Clear the timeout
      this.timeoutController.clear();
    }
  };

  private onSeeked_ = (): void => {
    let playheadTime = this.videoElement_.currentTime;
    if (this.isLive && this.shakaPlayer_.getPlayheadTimeAsDate()) {
      playheadTime = this.shakaPlayer_.getPlayheadTimeAsDate().getTime() / 1000;
    }
    this.emitter_.publish('playerseeked', playheadTime);
    /**
     * NOTE: The timeupdate listener is removed and re-added after a seek to prevent
     * duplicate listener registrations, which were causing excessive timeupdate events
     * per minute. This ensures only a single listener is active after seek completes.
     */
    this.videoElement_.removeEventListener('timeupdate', this.onTimeUpdate_);
    this.videoElement_.addEventListener('timeupdate', this.onTimeUpdate_);
  };

  private onPause_ = (): void => {
    this.emitter_.publish('playerpaused');
  };

  private onPlay_ = (): void => {
    this.emitter_.publish('playerstarted');
  };

  // Note : Commented onSuspend event to fix .mp4 & tizen playback failure issues and the reasons are given below:
  // The HTMLMediaElement.stalled event fires when media download has failed to progress for at least 3 seconds.
  // In Media Source Extensions, the web app manages the download and the media element is not aware of its progress.
  // Since some apps append media data in chunks larger than 3 seconds, stalled was being fired at inappropriate times.
  // To solve this, stalled has been removed for Media Source Extensions.
  // private onEnded_ = (): void => {
  //
  // }

  private onEnded_ = (): void => {
    this.emitter_.publish('playerended');
  };

  /**
   * Note: shaka player uses native HLS player for fairplay DRM by default from v4.8. Refer: {@link https://github.com/shaka-project/shaka-player/releases/tag/v4.8.0}.
   * In this mode, the video element directly handles the manifest and segment loading.
   * Therefore, to catch playback issues, you must listen for the error event on the video element itself.
   */
  private onVideoError_ = (): void => {
    const videoElementError = this.videoElement_?.error;
    const playerError = createError(
      PlayerErrorCodes.MEDIA_PLAYBACK_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_PLAYBACK,
      `Errors parsing or processing audio or video streams.`,
      videoElementError ? createError(videoElementError.code, JSON.stringify(videoElementError)) : undefined,
      videoElementError ? `${videoElementError.code} ${videoElementError.message}` : undefined,
    );
    this.emitter_.publish('error', playerError);
  };

  private onTimeUpdate_ = (): void => {
    if (this.initialPlaybackTime_ && PlatformUtil.isApple() && this.isLive) {
      this.videoElement_.removeEventListener('canplay', this.onCanPlay_);
      this.logger_.log(this.initialPlaybackTime_);
      this.seek(this.initialPlaybackTime_);
      this.initialPlaybackTime_ = undefined;
    }
    const currentTime = this.videoElement_.currentTime;
    let playheadTime = currentTime;
    // Contents from few Encoders returns the current time of video in seconds instead of epoch.
    // It has to be converted to epoch using getPresentationStartTime_().
    if (this.isLive) {
      playheadTime = this.getPresentationStartTime_() + currentTime;
    }

    PlatformUtil.isApple() && this.validateModeSwitched();
    this.emitter_.publish('timeupdate', currentTime, this.videoElement_.duration, playheadTime);
  };

  private onEmsgEvent_ = (event: any): void => {
    const emsg = Object.assign({ type: event.type }, event.detail);
    this.emitter_.publish('timedmetadata', emsg);
  };

  private registerNetworkFilters_ = (): void => {
    this.shakaPlayer_.getNetworkingEngine().registerRequestFilter((type: any, request: any) => {
      if (
        type === this.shakaLibrary.net.NetworkingEngine.RequestType.LICENSE &&
        this.drmScheme_ === DrmScheme.FAIRPLAY
      ) {
        request.headers['Content-Type'] = 'application/octet-stream';
        request.headers['Accept'] = 'application/octet-stream, application/json';
        this.logger_.debug('License Request payload =>', request.body);
      }
    });
  };

  /**
   * Reason: The player Live Manifest occurred with bad http status keeps on retries
   * We are listening to the retryStreaming callback to handle that as an error
   * Irrespective of shaka error category whether recoverable or critical.
   * If stopPlaybackOnManifestHttpErrorCodes[] configured with HTTP status codes (e.g., [404, 410]).
   * Playback will abort with an error only once Shaka has exhausted every configured retry
   * attempt for a manifest request that returns a matching bad status - not on the first retry.
   * Shaka's NetworkingEngine dispatches one 'retry' event per failed-but-recoverable attempt
   * (including the final attempt before it gives up), so once we've observed as many matching
   * 'retry' events as the configured manifest maxAttempts, retries are exhausted.
   * Reference Link: https://shaka-player-demo.appspot.com/docs/api/tutorial-errors.html#custom-handling-of-retries
   */

  private manifestErrorHandler = (event: any): void => {
    if (!Array.isArray(this.playbackOptions_?.stopPlaybackOnManifestHttpErrorCodes)) {
      return;
    }

    const code = event.error.code;
    const data = event.error.data;

    if (
      code === this.shakaLibrary.util.Error.Code.BAD_HTTP_STATUS &&
      Array.isArray(data) &&
      this.playbackOptions_?.stopPlaybackOnManifestHttpErrorCodes.includes(data[1]) &&
      data[4] === this.shakaLibrary.net.NetworkingEngine.RequestType.MANIFEST
    ) {
      this.manifestRetryAttemptCount_++;

      // Read the actual configured maxAttempts from Shaka itself (set in
      // setNetworkRetryConfigurations()), rather than re-deriving or hardcoding a default,
      // so this stays correct even if Shaka's own default changes across versions.
      // If it's ever missing (e.g. a malformed externalShakaPlayer config), the comparison
      // below naturally evaluates to false, so we fail safe by raising the error immediately.
      const maxManifestAttempts =
        this.playbackOptions_.networkRetries != null
          ? this.playbackOptions_.networkRetries + 1
          : this.shakaPlayer_.getConfiguration()?.manifest?.retryParameters?.maxAttempts;

      if (this.manifestRetryAttemptCount_ < maxManifestAttempts) {
        // Shaka still has retry attempts left for this manifest request; let it keep retrying.
        return;
      }

      this.manifestRetryAttemptCount_ = 0;

      const playerError: Error = createError(
        PlayerErrorCodes.MEDIA_SOURCE_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_PLAYBACK,
        'Failed to fetch manifest due to bad http status',
        undefined,
        event.error,
      );

      this.emitter_.publish('manifestHttpError', playerError);
    }
  };

  /**
  private setLoggerLevel_(): void {
    switch (this.logger_.logLevel) {
      case LoggerLevel.ERROR:
        this.shakaLibrary.log.setLevel(this.shakaLibrary.log.Level.ERROR);
        break;
      case LoggerLevel.WARN:
        this.shakaLibrary.log.setLevel(this.shakaLibrary.log.Level.WARNING);
        break;
      case LoggerLevel.INFO:
        this.shakaLibrary.log.setLevel(this.shakaLibrary.log.Level.INFO);
        break;
      case LoggerLevel.DEBUG:
        this.shakaLibrary.log.setLevel(this.shakaLibrary.log.Level.DEBUG);
        break;
      case LoggerLevel.VERBOSE:
        this.shakaLibrary.log.setLevel(this.shakaLibrary.log.Level.V2);
        break;
      default:
        this.shakaLibrary.log.setLevel(this.shakaLibrary.log.Level.V2);
        break;
    }
  }
  */
}
